import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'devices',
  slug: 'devices',
  name: 'Devices (RMM Inventory)',
  description: 'The fleet-wide device inventory: hardware, software, health, and cross-fleet software search.',
  icon: 'DevicesOther',
  endpoints: [
    {
      slug: 'list',
      method: 'GET',
      path: '/api/rmm-inv/devices',
      summary: 'List fleet devices',
      description: 'Returns the read-only device inventory for the tenant — hardware, OS, and last check-in for every enrolled endpoint.',
      auth: 'session',
      responseExample: `[
{
  "id": "dev_10ac",
  "hostname": "LIB-PC-04",
  "serial": "5CD1234ABC",
  "os": "Windows 11 Pro",
  "lastCheckIn": "2026-07-20T08:55:00.000Z"
}
]`,
      sourceFile: 'routes/rmm-inventory.js',
    },
    {
      slug: 'get',
      method: 'GET',
      path: '/api/rmm-inv/devices/:id',
      summary: 'Get device detail',
      description: 'Full inventory detail for one device: installed software, hardware components, and network identity.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      responseExample: `{
"id": "dev_10ac",
"hostname": "LIB-PC-04",
"software": [{ "name": "Google Chrome", "version": "126.0.6478.127" }],
"lastCheckIn": "2026-07-20T08:55:00.000Z"
}`,
      sourceFile: 'routes/rmm-inventory.js',
    },
    {
      slug: 'software-search',
      method: 'GET',
      path: '/api/rmm-inv/software',
      summary: 'Find devices by installed software',
      description: 'Searches the entire fleet for devices with a given software title installed — the fastest way to answer "which machines still have the vulnerable version."',
      auth: 'session',
      params: [{ name: 'q', in: 'query', type: 'string', required: true, description: 'Software name to search for (partial match).' }],
      responseExample: `{ "query": "7-Zip", "devices": [{ "id": "dev_10ac", "hostname": "LIB-PC-04", "version": "22.01" }] }`,
      sourceFile: 'routes/rmm-inventory.js',
    },
    {
      slug: 'tag',
      method: 'PUT',
      path: '/api/rmm-inv/devices/:id/tags',
      summary: 'Update device tags',
      description: 'Sets the admin-authoritative tag list on a device — agent-reported tags never override these.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      requestBodyExample: `{ "tags": ["library", "public-lab"] }`,
      responseExample: `{ "ok": true, "tags": ["library", "public-lab"] }`,
      sourceFile: 'routes/rmm-inventory.js',
    },
    {
      slug: 'summary',
      method: 'GET',
      path: '/api/rmm-inv/summary',
      summary: 'Get fleet summary rollup',
      description: 'Returns fleet-wide counts: devices by platform and OS, how many are stale (no check-in within the configured staleness window), pending patches, reboot-pending devices, and low-disk devices. Returns a `simulated: true` sample rollup when RMM inventory is not configured or has no enrolled devices yet, so the dashboard has something to render before the first agent checks in.',
      auth: 'session',
      responseExample: `{
  "simulated": false,
  "total": 42,
  "stale": 3,
  "byPlatform": { "windows": 38, "macos": 4 },
  "byOs": { "Windows 11 Pro 26100": 30, "Windows 10 Pro": 8, "macOS 15.4": 4 },
  "pendingPatchTotal": 17,
  "rebootPendingCount": 5,
  "lowDiskCount": 1,
  "staleAfterMin": 4320
}`,
      sourceFile: 'routes/rmm-inventory.js',
    },
    {
      slug: 'revoke',
      method: 'POST',
      path: '/api/rmm-inv/devices/:id/revoke',
      summary: 'Revoke a device',
      description: 'Marks a device as revoked. A revoked device\'s agent token no longer authenticates check-ins — use this to decommission a machine or respond to a lost or stolen device without deleting its historical inventory data.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/rmm-inventory.js',
    },
    {
      slug: 'missing-updates',
      method: 'GET',
      path: '/api/rmm-patch/devices/:id/missing',
      summary: 'List missing updates for a device',
      description: 'Returns only the still-missing patch records for one device — the patch planner\'s core read. Reporting only: this endpoint, and the rmm-patch module generally, never triggers an install. It summarizes what a collector last reported as missing so a human decides what to approve in a maintenance window.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      responseExample: `{
  "deviceId": "pdev_2f61",
  "hostname": "LIB-PC-04",
  "missing": [
    {
      "kb": "KB5040442",
      "updateId": "",
      "title": "2026-07 Cumulative Update for Windows 11",
      "severity": "critical",
      "classification": "security",
      "status": "missing",
      "releasedAt": "2026-07-09",
      "detectedAt": "2026-07-15T06:00:00.000Z",
      "rebootRequired": true
    }
  ],
  "count": 1,
  "simulated": false
}`,
      sourceFile: 'routes/rmm-patch.js, lib/rmm-patch.js',
    },
    {
      slug: 'compliance',
      method: 'GET',
      path: '/api/rmm-patch/compliance',
      summary: 'Get fleet patch-compliance rollup',
      description: 'Reporting-only fleet compliance summary: percent patched, missing-update counts by severity, the oldest still-missing patch\'s age, and how many devices have not checked in recently (counted honestly as stale, never silently dropped from the denominator). There is no endpoint anywhere in rmm-patch that applies or triggers a patch install — every endpoint in this module only reports what a collector observed.',
      auth: 'session',
      responseExample: `{
  "deviceCount": 42,
  "devicesWithData": 40,
  "patchedPct": 87.3,
  "missingBySeverity": { "critical": 4, "important": 9, "moderate": 2, "low": 0, "unknown": 1 },
  "oldestMissingDays": 41,
  "totals": { "missing": 16, "installed": 110, "failed": 0, "deferred": 2, "notApplicable": 5 },
  "rebootPending": 5,
  "staleDevices": 3,
  "simulated": false
}`,
      sourceFile: 'routes/rmm-patch.js, lib/rmm-patch.js',
    },
    {
      slug: 'asset-list',
      method: 'GET',
      path: '/api/devices',
      summary: 'List asset-tracked devices',
      description: 'Returns the tenant\'s checkout/checkin asset register — distinct from the RMM-managed fleet inventory above. Before returning, lazily projects any natively-enrolled RMM devices into this list (idempotent; only persists a write when something actually changed) so an enrolled machine shows up here too. Non-admin callers get a filtered, redacted view: only devices that are RMM-unmanaged AND (Available or assigned to them), with remote-control fields (remoteUrl, a1Console, detail, detailSource) stripped from every record.',
      auth: 'session',
      responseExample: `[
  {
    "id": "dev_7f2a91",
    "assetTag": "CHR-0512",
    "type": "Chromebook",
    "model": "Lenovo 300e Gen 3",
    "serial": "LN300E88231",
    "status": "Checked Out",
    "assignedTo": "Grace Liu",
    "room": "Room 214",
    "purchaseDate": "2024-08-12",
    "warrantyExpires": "2027-08-12",
    "eolDate": "2029-08-12",
    "cost": 329,
    "createdAt": "2024-08-15T14:02:00.000Z",
    "updatedAt": "2026-07-18T16:40:00.000Z"
  }
]`,
      sourceFile: 'routes/devices.js, lib/rmm-inventory-sync.js',
    },
    {
      slug: 'asset-create',
      method: 'POST',
      path: '/api/devices',
      summary: 'Create an asset record',
      description: 'Creates a new device/asset record through the cleanDevice() allowlist sanitizer — any field not on the allowlist is silently dropped, not rejected. assetTag is required; a request without one gets a 400. Requires the admin.technician.tenant grant (or admin) — a plain authenticated user cannot create asset records.',
      auth: 'session',
      requestBodyExample: `{
  "assetTag": "CHR-0513",
  "type": "Chromebook",
  "model": "Lenovo 300e Gen 3",
  "serial": "LN300E88244",
  "room": "IT Storage",
  "cost": 329,
  "purchaseDate": "2026-07-15"
}`,
      responseExample: `{
  "id": "dev_c81e04",
  "assetTag": "CHR-0513",
  "type": "Chromebook",
  "model": "Lenovo 300e Gen 3",
  "serial": "LN300E88244",
  "status": "Available",
  "room": "IT Storage",
  "cost": 329,
  "purchaseDate": "2026-07-15",
  "createdAt": "2026-07-21T13:05:00.000Z",
  "updatedAt": "2026-07-21T13:05:00.000Z"
}`,
      sourceFile: 'routes/devices.js',
    },
    {
      slug: 'asset-update',
      method: 'PUT',
      path: '/api/devices/:id',
      summary: 'Update an asset record',
      description: 'Replaces a device\'s allow-listed fields via the same cleanDevice() sanitizer used on create — this is a full replace of the allow-listed field set, not a partial patch, so omitted fields are cleared. Returns 404 if the id doesn\'t exist and 400 if assetTag is missing from the payload. Requires the admin.technician.tenant grant (or admin).',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      requestBodyExample: `{
  "assetTag": "CHR-0513",
  "type": "Chromebook",
  "model": "Lenovo 300e Gen 3",
  "serial": "LN300E88244",
  "room": "Room 118",
  "cost": 329,
  "purchaseDate": "2026-07-15",
  "warrantyExpires": "2029-07-15"
}`,
      responseExample: `{
  "id": "dev_c81e04",
  "assetTag": "CHR-0513",
  "room": "Room 118",
  "warrantyExpires": "2029-07-15",
  "updatedAt": "2026-07-21T13:40:00.000Z"
}`,
      sourceFile: 'routes/devices.js',
    },
    {
      slug: 'asset-delete',
      method: 'DELETE',
      path: '/api/devices/:id',
      summary: 'Delete an asset record',
      description: 'Hard-deletes the device record from the tenant\'s asset register. This does not cascade — checkout/checkin history rows referencing this deviceId are left in place (GET /api/devices/:id/history) but the parent device is gone. Requires the admin.technician.tenant grant (or admin).',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/devices.js',
    },
    {
      slug: 'asset-checkout',
      method: 'POST',
      path: '/api/devices/:id/checkout',
      summary: 'Check a device out',
      description: 'Assigns a device to a person and flips its status to "Checked Out". A non-admin can only check out a device that is currently Available and not RMM-managed, and if assignedTo is omitted the device is checked out to themselves; an admin can check out any device (including RMM-managed ones) to anyone, at any status. Every checkout appends an entry to the device\'s history log.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      requestBodyExample: `{ "assignedTo": "Grace Liu", "dueDate": "2026-08-15" }`,
      responseExample: `{
  "id": "dev_7f2a91",
  "assetTag": "CHR-0512",
  "status": "Checked Out",
  "assignedTo": "Grace Liu",
  "dueDate": "2026-08-15",
  "updatedAt": "2026-07-21T14:10:00.000Z"
}`,
      sourceFile: 'routes/devices.js',
    },
    {
      slug: 'asset-checkin',
      method: 'POST',
      path: '/api/devices/:id/checkin',
      summary: 'Check a device back in',
      description: 'Returns a device to "Available" and clears its assignment. A non-admin can only check in a device currently assigned to themselves or that isn\'t RMM-managed; an admin can check in anything. The history entry is written before the assignment fields are cleared, so the log still shows who it was checked in from.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      responseExample: `{
  "id": "dev_7f2a91",
  "assetTag": "CHR-0512",
  "status": "Available",
  "assignedTo": "",
  "dueDate": "",
  "updatedAt": "2026-07-21T15:00:00.000Z"
}`,
      sourceFile: 'routes/devices.js',
    },
    {
      slug: 'asset-history',
      method: 'GET',
      path: '/api/devices/:id/history',
      summary: 'Get a device\'s checkout history',
      description: 'Returns the most recent 50 checkout/checkin events for one device, newest first. Visibility mirrors the device itself: a non-admin gets a 403 for an RMM-managed device, or for a device that\'s neither Available nor currently theirs.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Device id.' }],
      responseExample: `[
  { "id": "hist_9a01", "deviceId": "dev_7f2a91", "action": "checkin", "person": "Grace Liu", "by": "mrivera", "at": "2026-07-15T09:12:00.000Z" },
  { "id": "hist_88f2", "deviceId": "dev_7f2a91", "action": "checkout", "person": "Grace Liu", "by": "grace.liu", "at": "2026-06-01T08:30:00.000Z" }
]`,
      sourceFile: 'routes/devices.js',
    },
    {
      slug: 'lifecycle',
      method: 'GET',
      path: '/api/devices/lifecycle',
      summary: 'Get fleet warranty/EOL lifecycle rollup',
      description: 'Admin-only fleet-wide replacement-planning view: counts of expired warranties, warranties expiring within 30/60/90 days, devices approaching end-of-life within 90 days, tracked vs. untracked device counts, total fleet value, and unit counts grouped by purchase year. Retired devices are excluded from every expiring/EOL bucket.',
      auth: 'session',
      responseExample: `{
  "counts": { "expired": 2, "expiring30": 3, "expiring60": 5, "expiring90": 8, "eolSoon": 4, "tracked": 210, "untracked": 12 },
  "totalValue": 148230.50,
  "byYear": { "2023": 64, "2024": 88, "2025": 58, "2026": 12 }
}`,
      sourceFile: 'routes/devices.js',
    },
  ],
};
